import {
  applySnapshot,
  flow,
  getSnapshot,
  Instance,
  SnapshotIn,
  types,
} from "mobx-state-tree";
import {
  BoundedNameModel,
  createBoundedNameModel,
} from "../../models/BoundedNameModel";
import {
  createKeycloakEmailModel,
  KeycloakEmailModel,
} from "../../models/KeycloakEmailModel";
import { GetOptOutScreenDetailsRPC } from "@pulse/pulse-rpcs/lib/email-cycle/getOptOutScreenDetailsRPC";
import { getAPIClient } from "../../networking/APIClient";
import { LeoUUID } from "@surya-digital/leo-ts-runtime";
import { LeoRPCResult } from "@surya-digital/leo-ts-types";
import { useGetOptOutScreenDetailsRPCClientImpl } from "../rpcs/RPC";
import {
  createOptOutOfCampaignStore,
  OptOutOfCampaignStore,
} from "./OptOutOfCampaignStore";

const getInitialState = (): SnapshotIn<typeof OptOutScreenDetailsStore> => ({
  projectName: createBoundedNameModel(),
  email: createKeycloakEmailModel(),
  loadingOptOutScreenDetails: true,
  getOptOutScreenDetailsRPCError: null,
  optOutOfCampaignStore: createOptOutOfCampaignStore(),
});

export const OptOutScreenDetailsStore = types
  .model("OptOutScreenDetailsStore", {
    projectName: BoundedNameModel,
    email: KeycloakEmailModel,
    loadingOptOutScreenDetails: types.boolean,
    getOptOutScreenDetailsRPCError: types.maybeNull(
      types.enumeration(
        "GetOptOutScreenDetailsRPCError",
        Object.values(GetOptOutScreenDetailsRPC.RPCError),
      ),
    ),
    optOutOfCampaignStore: OptOutOfCampaignStore,
  })
  .actions((store) => ({
    getOptOutScreenDetails: flow(function* (emailId: string) {
      try {
        store.loadingOptOutScreenDetails = true;
        store.getOptOutScreenDetailsRPCError = null;

        const apiClient = getAPIClient(store);
        const request = new GetOptOutScreenDetailsRPC.Request(
          new LeoUUID(emailId),
        );

        const result: LeoRPCResult<
          GetOptOutScreenDetailsRPC.Response,
          GetOptOutScreenDetailsRPC.Errors.Errors
        > =
          yield useGetOptOutScreenDetailsRPCClientImpl(apiClient).execute(
            request,
          );

        if ("response" in result) {
          store.projectName.setName(result.response.projectName.name);
          store.email.setValue(result.response.email.email);
          return;
        }

        switch (result.error.code) {
          case GetOptOutScreenDetailsRPC.RPCError.InvalidEmailId:
            store.getOptOutScreenDetailsRPCError = result.error.code;
            break;
          default:
            result.error.code satisfies never;
        }
      } finally {
        store.loadingOptOutScreenDetails = false;
      }
    }),
    resetStore: (): void => {
      const { projectName, email } = getInitialState();
      applySnapshot(store, { ...getSnapshot(store), projectName, email });
    },
  }));

export const createOptOutScreenDetailsStore = (): Instance<
  typeof OptOutScreenDetailsStore
> => OptOutScreenDetailsStore.create(getInitialState());
